import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import MD5 from "md5-es";
import { Observable, of, Subject, throwError, TimeoutError } from "rxjs";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";

import { LocalStorage, SessionStorage } from "@dffedb/util";
import { DffError } from "@dffedb/util";
import { Rolle } from "@e-forsyning/common/bruger";
import { AppserverBrugerCredentialsRepository, AppserverInstallationCredentialsRepository } from "@e-forsyning/common/credentials";
import { Installation, InstallationService } from "@e-forsyning/common/installation";
import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";
import { convertAppserverUrlToLocalProxy, erTeknikerSpaerretFejl, TEKNIKER_SPAERRET_BESKED } from "@e-forsyning/common/util";

import { AuthenticationResult } from "./authentication-result.model";
import { AuthenticationService } from "./authentication.service";
import { HuskMigCredentialsService } from "./husk-mig-credentials.service";
import { SessionReauthCredentialsService } from "./session-reauth-credentials.service";
import { resolveSkalSkifteKodeord } from "./skal-skifte-kodeord.util";
import { APP_SHELL_LAYOUT_OPTIONS } from "../app-shell/app-shell-layout.token";
import { FeatureFlagService } from "@e-forsyning/features/feature-flag";

const SKAL_SKIFTE_KODEORD_KEY = "tekniker.skalSkifteKodeord";

export class ServiceProRequiredError extends DffError {
    constructor() {
        super("Tekniker-login er flyttet til ServicePro.");
        Object.setPrototypeOf(this, ServiceProRequiredError.prototype);
    }
}

@Injectable({ providedIn: "root" })
export class LoginService {
    private readonly appShellLayout = inject(APP_SHELL_LAYOUT_OPTIONS, { optional: true });
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly sessionReauthCredentials = inject(SessionReauthCredentialsService);
    private readonly indstillinger$: Observable<any>;
    /** Emitter ved logout så komponenter kan rydde subscriptions (fx hentMaalerOplysningerForAktuelInstallation). */
    private readonly logoutSubject = new Subject<void>();
    public readonly logout$ = this.logoutSubject.asObservable();

    constructor(
        private readonly authenticationService: AuthenticationService,
        private readonly brugerCredentials: AppserverBrugerCredentialsRepository,
        private readonly http: HttpClient,
        private readonly installationCredentials: AppserverInstallationCredentialsRepository,
        private readonly installationService: InstallationService,
        private readonly huskMigCredentialsService: HuskMigCredentialsService,
        private readonly indstillinger: UmbracoIndstillingerObserver,
        private readonly sessionStorage: SessionStorage,
        private readonly localStorage: LocalStorage
    ) {
        this.indstillinger$ = this.indstillinger.valueChanges.pipe(map((u) => u.aktuelEforsyning));
    }

    /** True når tekniker skal skifte til nyt password før appen må bruges. */
    public skalSkifteKodeord(): boolean {
        return this.sessionStorage.getItem(SKAL_SKIFTE_KODEORD_KEY) === "1";
    }

    public setSkalSkifteKodeord(value: boolean): void {
        if (value) {
            this.sessionStorage.setItem(SKAL_SKIFTE_KODEORD_KEY, "1");
        } else {
            this.sessionStorage.removeItem(SKAL_SKIFTE_KODEORD_KEY);
        }
    }

    public loginMedGemteCredentials(): Observable<AuthenticationResult> {
        const userCredentials = this.localStorage.getItem("userCredentials");
        const credentials = (userCredentials && JSON.parse(atob(userCredentials))) as { username: string; password: string };
        return credentials ? this.login(credentials.username, credentials.password, true) : throwError("Kan ikke udføre autologin uden gemte credentials");
    }

    public loginAsForbruger(brugernavn: string, kodeord: string, huskMig: boolean): Observable<AuthenticationResult> {
        brugernavn = this.fixCasing((brugernavn || "").toString());
        kodeord = (kodeord || "").toString();
        const hashedKodeord = this.hashKodeord(brugernavn, kodeord);

        return this.authenticate(brugernavn, hashedKodeord).pipe(
            switchMap((result) => {
                if (!result.success) {
                    return of(result);
                }
                // TODO: Maybe this can be done more efficiently?
                // see if we can filter by role directly in the api call as the cryptId is fetched
                return this.hentBruger(result.cryptId).pipe(
                    switchMap((bruger: any) => {
                        if (bruger?.rolle === 9) {
                            const denied: AuthenticationResult = {
                                ...result,
                                success: false,
                                deniedRole: true
                            };
                            return of(denied);
                        }

                        this.sessionStorage.setItem("user", JSON.stringify(bruger));
                        return this.gemCredentials(result.cryptId, brugernavn).pipe(
                            tap(() => this.gemHuskMigCredentials(brugernavn, hashedKodeord, huskMig)),
                            tap(() => this.sessionReauthCredentials.gem(brugernavn, hashedKodeord)),
                            switchMap(() => this.gemAktuelInstallation()),
                            map(() => result)
                        );
                    })
                );
            }),
            catchError((error: any) => {
                console.error("[login-debug] loginAsForbruger error", error);
                return throwError(this.createError(error));
            })
        );
    }

    public loginAsTechnician(brugernavn: string, kodeord: string, huskMig: boolean): Observable<AuthenticationResult> {
        brugernavn = this.fixCasing((brugernavn || "").toString());
        kodeord = (kodeord || "").toString();
        const hashedKodeord = this.hashKodeord(brugernavn, kodeord);

        return this.authenticate(brugernavn, hashedKodeord).pipe(
            switchMap((result) => {
                if (!result.success) {
                    return of(result);
                }
                // TODO: Maybe this can be done more efficiently?
                // see if we can filter by role directly in the api call as the cryptId is fetched
                return this.hentBruger(result.cryptId).pipe(
                    switchMap((bruger: any) => {
                        if (bruger?.rolle !== 9) {
                            const denied: AuthenticationResult = {
                                ...result,
                                success: false,
                                deniedRole: true
                            };
                            return of(denied);
                        }

                        const skalSkifte = resolveSkalSkifteKodeord(bruger);
                        this.setSkalSkifteKodeord(skalSkifte);
                        this.sessionStorage.setItem("user", JSON.stringify(bruger));
                        return this.gemCredentials(result.cryptId, brugernavn).pipe(
                            tap(() => {
                                this.sessionReauthCredentials.gem(brugernavn, hashedKodeord);
                                if (!skalSkifte) {
                                    this.gemHuskMigCredentials(brugernavn, hashedKodeord, huskMig);
                                }
                            }),
                            switchMap(() => this.gemAktuelInstallation()),
                            map(() => new AuthenticationResult(result.success, result.cryptId, result.deniedRole, skalSkifte))
                        );
                    })
                );
            }),
            catchError((error: any) => {
                if (erTeknikerSpaerretFejl(error)) {
                    return throwError(() => new DffError(TEKNIKER_SPAERRET_BESKED, { friendlyMessage: TEKNIKER_SPAERRET_BESKED, innerError: error }));
                }
                console.error("[login-debug] loginAsTechnician error", error);
                return throwError(this.createError(error));
            })
        );
    }

    public login(brugernavn: string, kodeord: string, huskMig: boolean): Observable<AuthenticationResult> {
        brugernavn = this.fixCasing((brugernavn || "").toString());
        kodeord = (kodeord || "").toString();
        const hashedKodeord = this.hashKodeord(brugernavn, kodeord);
        return this.authenticate(brugernavn, hashedKodeord).pipe(
            switchMap((result) => {
                if (!result.success) {
                    return of(result);
                }
                return this.hentBruger(result.cryptId).pipe(
                    switchMap((bruger) => {
                        if (
                            this.featureFlagService.isFeatureEnabled() &&
                            Rolle.erFofBruger(bruger?.rolle) &&
                            this.appShellLayout?.teknikerLoginRoute !== "/servicepro-login"
                        ) {
                            this.logout();
                            return throwError(() => new ServiceProRequiredError());
                        }
                        return this.gemCredentials(result.cryptId, brugernavn).pipe(
                            tap(() => {
                                this.sessionStorage.setItem("user", JSON.stringify(bruger));
                                this.sessionReauthCredentials.gem(brugernavn, hashedKodeord);
                            })
                        );
                    }),
                    switchMap(() => {
                        // Biometric/husk-mig går ofte via login(); sæt flag fra getebrugerinfo for teknikere.
                        try {
                            const bruger = JSON.parse(this.sessionStorage.getItem("user") || "{}");
                            if (Rolle.erFofBruger(bruger?.rolle)) {
                                const skalSkifte = resolveSkalSkifteKodeord(bruger);
                                this.setSkalSkifteKodeord(skalSkifte);
                                if (!skalSkifte) {
                                    this.gemHuskMigCredentials(brugernavn, hashedKodeord, huskMig);
                                }
                                return of(new AuthenticationResult(true, result.cryptId, false, skalSkifte));
                            }
                        } catch {
                            // ignore
                        }
                        this.gemHuskMigCredentials(brugernavn, hashedKodeord, huskMig);
                        return of(result);
                    }),
                    switchMap((authResult) => this.gemAktuelInstallation().pipe(map(() => authResult)))
                );
            }),
            catchError((error: any) => {
                if (error instanceof ServiceProRequiredError) {
                    return throwError(() => error);
                }
                console.error("[login-debug] login error", error);
                return throwError(this.createError(error));
            })
        );
    }

    public logout(): void {
        this.sessionStorage.removeItem("currentUser");
        this.sessionStorage.removeItem("user");
        // this.sessionStorage.removeItem("vaerksData");
        this.sessionStorage.setItem("credentials", "{}");
        this.sessionStorage.removeItem("installationer");
        this.sessionReauthCredentials.clear();
        this.setSkalSkifteKodeord(false);
        try {
            this.sessionStorage.setItem("login.silentAttempted", "1");
        } catch {}
        this.installationCredentials.notifyLogout();
        this.logoutSubject.next();
    }

    public erLoggetInd(forsyningNodeId?: number): boolean {
        const isAuthenticated = !!this.installationCredentials.hent().cryptId;
        const currentForsyningNodeId = this.installationCredentials.hent().forsyningNodeId;

        if (!isAuthenticated) {
            return false;
        }

        if (!currentForsyningNodeId) {
            return false;
        }

        if (!forsyningNodeId) {
            // Vi spørger om man er logget ind på en hvilken som helst eForsyning.
            return true;
        }

        return forsyningNodeId === currentForsyningNodeId;
    }

    public tilknytEmail(forbrugernr: string, kode: string, email: string, kodeord: string): Observable<void> {
        email = this.fixCasing(email);
        const hashedPinkode = this.hashKodeord(forbrugernr, kode);
        const hashedPassword = this.hashKodeord(email, kodeord);
        return this.indstillinger$.pipe(
            switchMap((indstillinger) => {
                const url = convertAppserverUrlToLocalProxy(indstillinger.appServerUrl) + "api/tilknytemail";
                return this.http.post(url, { forbrugernr, hashedPinkode, email, hashedPassword }, { responseType: "text" }).pipe(map(() => null));
            })
        );
    }

    public glemtKodeord(email: string): Observable<void> {
        email = this.fixCasing(email);
        return this.indstillinger$.pipe(
            switchMap((indstillinger) => {
                const url = convertAppserverUrlToLocalProxy(indstillinger.appServerUrl) + "api/glemtpwd";
                const vaerkurl = indstillinger.siteUrl + "#/";
                return this.http.post(url, { email, vaerkurl }, { responseType: "text" }).pipe(map(() => null));
            })
        );
    }

    /**
     * ServicePro-tekniker: send sæt/glemt-password-mail (initialer eller e-mail).
     * Bruger aktuel forsyningId + ServicePro-origin som base-URL i mail-linket.
     */
    public teknikerGlemtPwd(bruger: string): Observable<string> {
        const fixedBruger = this.fixCasing(bruger);
        if (!fixedBruger) {
            return throwError(new DffError("Indtast Bruger-Id eller e-mail."));
        }

        return this.indstillinger$.pipe(
            take(1),
            switchMap((indstillinger) => {
                const forsyningId = (indstillinger?.forsyningId || "").trim();
                if (!forsyningId) {
                    return throwError(new DffError("Vælg værk før du kan anmode om ny adgangskode."));
                }

                const serviceprourl = typeof window !== "undefined" ? `${window.location.origin}/` : "";
                const configuredAppServerUrl = (indstillinger?.appServerUrl || "").trim();
                const appServerUrl$ = configuredAppServerUrl ? of(configuredAppServerUrl) : this.authenticationService.hentAppServerUrl(forsyningId);

                return appServerUrl$.pipe(
                    switchMap((appServerUrl) => {
                        const url = convertAppserverUrlToLocalProxy(appServerUrl) + "api/teknikerglemtpwd";
                        return this.http.post(url, { bruger: fixedBruger, serviceprourl, forsyningid: forsyningId }, { responseType: "text" });
                    })
                );
            })
        );
    }

    public resetPwd(kodeord: string, secGUID: string): Observable<void> {
        const saltedPwd = MD5.hash(kodeord);

        return this.indstillinger$.pipe(
            switchMap((indstillinger) => {
                const url = convertAppserverUrlToLocalProxy(indstillinger.appServerUrl) + "api/resetpwd/";
                return this.http.post(url, { saltedPwd, secGUID }, { responseType: "text" }).pipe(map(() => null));
            })
        );
    }

    /**
     * ServicePro-tekniker: gem nyt password via mail-token.
     * Resolver appserver fra forsyningId (mail-link query-param).
     */
    public teknikerResetPwd(kodeord: string, secGUID: string, forsyningId: string): Observable<void> {
        const saltedPwd = MD5.hash(kodeord);
        const trimmedForsyningId = (forsyningId || "").trim();
        if (!trimmedForsyningId) {
            return throwError(new DffError("Mangler forsyningId. Kan ikke nulstille adgangskode."));
        }

        return this.authenticationService.hentAppServerUrl(trimmedForsyningId).pipe(
            switchMap((appServerUrl) => {
                const baseUrl = convertAppserverUrlToLocalProxy(appServerUrl);
                if (!baseUrl) {
                    return throwError(new DffError(`Kunne ikke finde appserver for forsyningId "${trimmedForsyningId}". Tjek Umbraco AppServerUri på eForsyning-noden.`));
                }
                const url = `${baseUrl.replace(/\/?$/, "/")}api/teknikerresetpwd`;
                return this.http.post(url, { saltedPwd, secGUID }, { responseType: "text" }).pipe(map(() => null));
            })
        );
    }

    /**
     * ServicePro-tekniker: skift password mens man er logget ind (tvungen migrering eller frivilligt fra Brugerinfo).
     * Kræver appserver `api/teknikerskiftpwd` + gyldig cryptId.
     */
    public teknikerSkiftPwd(kodeord: string): Observable<void> {
        const saltedPwd = MD5.hash(kodeord);
        const cryptId = (this.installationCredentials.hent()?.cryptId || this.brugerCredentials.hent()?.cryptId || "").trim();
        if (!cryptId) {
            return throwError(new DffError("Du er ikke logget ind. Log ind igen for at skifte adgangskode."));
        }

        return this.indstillinger$.pipe(
            take(1),
            switchMap((indstillinger) => {
                const configuredAppServerUrl = (indstillinger?.appServerUrl || "").trim();
                const forsyningId = (indstillinger?.forsyningId || "").trim();
                const appServerUrl$ = configuredAppServerUrl
                    ? of(configuredAppServerUrl)
                    : forsyningId
                      ? this.authenticationService.hentAppServerUrl(forsyningId)
                      : throwError(new DffError("Mangler værk/appserver. Kan ikke skifte adgangskode."));

                return appServerUrl$.pipe(
                    switchMap((appServerUrl) => {
                        const baseUrl = convertAppserverUrlToLocalProxy(appServerUrl).replace(/\/?$/, "/");
                        const url = `${baseUrl}api/teknikerskiftpwd?id=${encodeURIComponent(cryptId)}`;
                        return this.http.post(url, { saltedPwd }, { responseType: "text" }).pipe(
                            tap(() => {
                                this.setSkalSkifteKodeord(false);
                                this.sessionReauthCredentials.opdaterKodeord(saltedPwd);
                            }),
                            map(() => null)
                        );
                    })
                );
            })
        );
    }

    private fixCasing(brugernavn: string): string {
        brugernavn = brugernavn || "";
        const erEmail = brugernavn.indexOf("@") > -1;
        return erEmail ? brugernavn.toLowerCase() : brugernavn.toUpperCase();
    }

    private authenticate(brugernavn: string, hashedKodeord: string): Observable<AuthenticationResult> {
        return this.indstillinger$.pipe(
            take(1),
            switchMap((indstillinger) => {
                const forsyningId = indstillinger?.forsyningId;
                if (!forsyningId) {
                    return throwError(
                        new DffError("Mangler forsyningId for valgt værk. Kan ikke logge ind.", {
                            innerError: { forsyningId, nodeId: indstillinger?.nodeId }
                        })
                    );
                }
                return this.authenticationService.authenticate(brugernavn, hashedKodeord, forsyningId, indstillinger?.appServerUrl);
            })
        );
    }

    private gemCredentials(cryptId: string, brugerNavn?: string): Observable<void> {
        const brugerCredentials = this.brugerCredentials.hent();
        const installationCredentials = this.installationCredentials.hent();
        brugerCredentials.cryptId = cryptId;
        if (brugerNavn) {
            brugerCredentials.brugerNavn = brugerNavn;
        }
        return this.indstillinger$.pipe(
            tap((indstillinger) => {
                brugerCredentials.forsyningId = indstillinger.forsyningId;
                brugerCredentials.forsyningNodeId = indstillinger.nodeId;
                this.brugerCredentials.gem(brugerCredentials);
                // I tilfælde af re-login fordi cryptId er udløbet, ønsker vi at beholde den valgte installation.
                // Dette kan dog kun ske, hvis vi ikke arbejder på tværs af forsyninger.
                const newInstallationCredentials =
                    installationCredentials.forsyningNodeId === brugerCredentials.forsyningNodeId
                        ? { ...installationCredentials, ...brugerCredentials }
                        : brugerCredentials;
                this.installationCredentials.gem(newInstallationCredentials);
                this.installationCredentials.gem(
                    installationCredentials.forsyningNodeId === brugerCredentials.forsyningNodeId
                        ? { ...installationCredentials, ...brugerCredentials }
                        : brugerCredentials
                );
            }),
            map(() => null)
        );
    }

    private gemAktuelInstallation(): Observable<void> {
        // Teknikere (FofBruger) har potentielt mange tusinde installationer og skal selv
        // vælge via install-picker eller adressesøgning. Det er både dyrt og meningsløst at
        // hente "første installation" automatisk for dem ved login.
        if (this.erTeknikerLoggetInd()) {
            return of(null);
        }
        return this.hentFoersteInstallation().pipe(
            tap((installation) => {
                this.gemInstallation(installation);
            }),
            map(() => null)
        );
    }

    private erTeknikerLoggetInd(): boolean {
        const raw = this.sessionStorage.getItem("user");
        if (!raw) {
            return false;
        }
        try {
            const bruger = JSON.parse(raw) as { rolle?: Rolle };
            return Rolle.erFofBruger(bruger?.rolle);
        } catch {
            return false;
        }
    }

    private gemInstallation(installation: Installation): void {
        const credentials = this.installationCredentials.hent();
        credentials.ejendomNr = installation && installation.ejendomNr;
        credentials.aktivNr = installation && installation.aktivNr;
        credentials.installationNr = installation && installation.installationNr;
        this.installationCredentials.gem(credentials);
    }

    private hentFoersteInstallation(): Observable<Installation> {
        return this.hentInstallationer().pipe(map((installationer) => installationer[0]));
    }

    private hentInstallationer(): Observable<Installation[]> {
        return this.installationService
            .hentEgneInstallationer()
            .pipe(
                switchMap((installationer) =>
                    installationer && installationer.length !== 0
                        ? of(installationer)
                        : this.installationService.hentEgneOgTilknyttedeInterneInstallationer().pipe(map((r) => r.items))
                )
            );
    }

    private gemBruger(cryptId: string): Observable<void> {
        return this.hentBruger(cryptId).pipe(
            // TODO: Fjernes når vi engang har flyttet alt over på Angular 8+ og ikke gemmer bruger i sessionStorage
            tap((bruger: any) => {
                this.sessionStorage.setItem("user", JSON.stringify(bruger));
            })
        );
    }

    private hentBruger(cryptId: string): Observable<any> {
        // TODO: Flyt til BrugerService?
        return this.indstillinger$.pipe(
            switchMap((indstillinger) => {
                const url = `${convertAppserverUrlToLocalProxy(indstillinger.appServerUrl)}api/getebrugerinfo/id/${cryptId}`;
                return this.http.get(url).pipe(
                    map((result: any) => {
                        const b = result || {};
                        b.adresse = b.vej + " " + b.husnr + " " + b.litra + " " + b.litra2;
                        b.postnrby = b.postnr + " " + b.bynavn;
                        b.erForbruger = () => b.rolle === 1;
                        return b;
                    })
                );
            })
        );
    }

    private hashKodeord(brugernavn: string, kodeord: string): string {
        const huskMigCredentials = this.huskMigCredentialsService.hent();
        return huskMigCredentials.brugernavn === brugernavn && huskMigCredentials.kodeord === kodeord ? kodeord : MD5.hash(kodeord);
    }

    private gemHuskMigCredentials(brugernavn: string, hashedKodeord: string, huskMig: boolean): void {
        this.huskMigCredentialsService.clear();
        if (huskMig) {
            this.huskMigCredentialsService.gem({ brugernavn, kodeord: hashedKodeord, huskMig });
        }
    }

    private createError(error: any): DffError {
        if (error instanceof TimeoutError) {
            return new DffError("Login tog for lang tid. Tjek netværk og valgt værk, og prøv igen.", { innerError: error });
        }
        return new DffError("Der er opstået en fejl i forbindelse med login", { innerError: error });
    }
}
